/**
 * ContactFormRequest — New Issue (Atollon API).
 * POST {origin}/svc/contact_form/issue
 * @see https://help.atollon.com/books/atollon-api/page/contactformrequest-service
 */

/**
 * Base URL for ContactForm API when not using same-origin (e.g. static bundle on another host).
 * Local dev: leave unset and use same-origin + package.json proxy so headers match curl.
 */
export function getContactFormOrigin() {
  if (typeof window === 'undefined') return '';
  const override = process.env.REACT_APP_CONTACT_FORM_ORIGIN;
  if (override && String(override).trim() !== '') {
    return String(override).replace(/\/$/, '');
  }
  return window.location.origin;
}

function getContactFormSecKey() {
  const key = process.env.REACT_APP_CONTACT_FORM_SECKEY;
  if (key && String(key).trim() !== '') {
    return String(key).trim();
  }
  return '';
}

/**
 * @param {Record<string, unknown>} payload - pub JSON
 * @returns {{ requestType: string, requestServiceEnabled: boolean, defaultTargetItemId: string|null }}
 *
 * Medtech test defaults (overridable via payload.serviceRequest or CRA env):
 * - REACT_APP_CONTACT_FORM_REQUEST_TYPE → issue template name (e.g. "QR Service Portal")
 * - REACT_APP_CONTACT_FORM_LEAD_CATEGORY → FPATypeCategory (e.g. client category)
 */
export function getContactFormConfig(payload) {
  const sr =
    payload?.serviceRequest && typeof payload.serviceRequest === 'object'
      ? payload.serviceRequest
      : null;

  const requestType =
    (typeof sr?.issueTemplateName === 'string' && sr.issueTemplateName.trim() !== ''
      ? sr.issueTemplateName.trim()
      : null) ||
    (typeof sr?.requestType === 'string' && sr.requestType.trim() !== ''
      ? sr.requestType.trim()
      : null) ||
    (process.env.REACT_APP_CONTACT_FORM_ISSUE_TEMPLATE &&
    String(process.env.REACT_APP_CONTACT_FORM_ISSUE_TEMPLATE).trim() !== ''
      ? String(process.env.REACT_APP_CONTACT_FORM_ISSUE_TEMPLATE).trim()
      : null) ||
    (process.env.REACT_APP_CONTACT_FORM_REQUEST_TYPE &&
    String(process.env.REACT_APP_CONTACT_FORM_REQUEST_TYPE).trim() !== ''
      ? String(process.env.REACT_APP_CONTACT_FORM_REQUEST_TYPE).trim()
      : null) ||
    'QR Service Portal';

  const rawId = sr?.defaultTargetItemId;
  const defaultTargetItemId =
    rawId != null && rawId !== '' ? String(rawId) : null;

  return {
    requestType,
    requestServiceEnabled: sr?.enabled === true,
    defaultTargetItemId,
  };
}

export function getContactFormLeadCategory() {
  const v = process.env.REACT_APP_CONTACT_FORM_LEAD_CATEGORY;
  if (v && String(v).trim() !== '') return String(v).trim();
  return 'com.atollon.project.fpatypecategory.client';
}

export function canSubmitContactForm(cfg) {
  return cfg.requestServiceEnabled === true;
}

/**
 * @returns {string} absolute URL
 */
export function buildContactFormIssueUrl() {
  const path = '/svc/contact_form/issue';
  if (typeof window !== 'undefined') {
    const isLocalDev =
      process.env.NODE_ENV === 'development' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1');
    // Same-origin + CRA proxy → medtech (matches server-side curl); avoids CORS on custom header.
    if (isLocalDev) {
      return new URL(path, window.location.origin).toString();
    }
  }
  const origin = getContactFormOrigin();
  return new URL(path, origin).toString();
}

/**
 * @param {object} params
 * @param {Record<string, unknown>} params.body - JSON body per API (incl. requestType)
 */
export async function submitContactFormIssue({ body }) {
  const url = buildContactFormIssueUrl();
  const secKey = getContactFormSecKey();
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (secKey) headers['X-Atollon-ContactRequest-SecKey'] = secKey;

  const res = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'omit',
    body: JSON.stringify(body),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    try {
      data = await res.text();
    } catch {
      data = null;
    }
  }

  return { res, data, url };
}
